// Lightweight SEO helper to manage document title and meta/link tags in a SPA

/**
 * Ensure a <meta> tag (by name or property) exists and is updated
 */
function upsertMeta({ key, value, content }) {
  if (!key || !value) return;
  const selector = `meta[${key}="${value}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, String(value));
    document.head.appendChild(el);
  }
  el.setAttribute('content', content ?? '');
}

/**
 * Ensure a <link rel="canonical"> exists and is updated
 */
function upsertCanonical(url) {
  if (!url) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function toAbsoluteUrl(possiblePath) {
  if (!possiblePath) return undefined;
  try {
    if (/^https?:\/\//i.test(possiblePath)) return possiblePath;
    const origin = window.location.origin.replace(/\/$/, '');
    const path = possiblePath.startsWith('/') ? possiblePath : `/${possiblePath}`;
    return `${origin}${path}`;
  } catch {
    return possiblePath;
  }
}

/**
 * Set common SEO tags for a page view
 */
export function setSeo({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords,
  noindex = false,
}) {
  try {
    if (title) {
      document.title = title;
      upsertMeta({ key: 'property', value: 'og:title', content: title });
      upsertMeta({ key: 'name', value: 'twitter:title', content: title });
    }

    if (description) {
      upsertMeta({ key: 'name', value: 'description', content: description });
      upsertMeta({ key: 'property', value: 'og:description', content: description });
      upsertMeta({ key: 'name', value: 'twitter:description', content: description });
    }

    if (keywords) {
      upsertMeta({ key: 'name', value: 'keywords', content: Array.isArray(keywords) ? keywords.join(', ') : String(keywords) });
    }

    if (url) {
      upsertCanonical(url);
      upsertMeta({ key: 'property', value: 'og:url', content: url });
    }

    const absoluteImage = toAbsoluteUrl(image);
    if (absoluteImage) {
      upsertMeta({ key: 'property', value: 'og:image', content: absoluteImage });
      upsertMeta({ key: 'name', value: 'twitter:image', content: absoluteImage });
      upsertMeta({ key: 'name', value: 'twitter:card', content: 'summary_large_image' });
    }

    if (type) {
      upsertMeta({ key: 'property', value: 'og:type', content: type });
    }

    upsertMeta({ key: 'property', value: 'og:site_name', content: 'VZBL' });

    // Robots
    upsertMeta({ key: 'name', value: 'robots', content: noindex ? 'noindex,nofollow' : 'index,follow' });
  } catch {
    // no-op
  }
}

/**
 * Convenience: derive a brief description from project sections
 */
export function buildProjectDescription(project) {
  if (!project) return undefined;
  try {
    const sections = project.projectContent?.sections || [];
    // Prefer explicit text section
    const textSection = sections.find((s) => s.type === 'text' && s.text);
    const raw = Array.isArray(textSection?.text)
      ? textSection.text.join(' ')
      : (textSection?.text || '');
    const cleaned = raw.replace(/\s+/g, ' ').trim();
    if (cleaned) return cleaned.length > 300 ? `${cleaned.slice(0, 297)}...` : cleaned;
  } catch {}
  return undefined;
}

/**
 * Convenience: pick a representative image for a project
 */
export function pickProjectImage(project) {
  if (!project) return undefined;
  if (project.thumbnail) return project.thumbnail;
  const sections = project.projectContent?.sections || [];
  const media = sections.find((s) => s.type === 'media' && (s.media || s.imageName));
  return media?.media || media?.imageName || undefined;
}


